import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Send, MessageSquare } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const ROLE_LABELS = { admin: "Admin", worker: "Care Coordinator", user: "Patient" };
const ROLE_COLORS = {
  admin: "bg-red-100 text-red-600",
  worker: "bg-purple-100 text-purple-700",
  user: "bg-blue-100 text-blue-700",
};

export default function TicketChat({ ticketId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/messages/${ticketId}`, { withCredentials: true });
      setMessages(res.data);
      setError(null);
    } catch (e) {
      if (e.response?.status === 403) setError("You don't have access to this conversation.");
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 4000);
    return () => clearInterval(interval);
  }, [ticketId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/${ticketId}`, { text: text.trim() }, { withCredentials: true });
      setText("");
      await fetchMessages();
      inputRef.current?.focus();
    } catch {}
    setSending(false);
  };

  if (error) return (
    <div className="p-4 text-center text-sm text-red-500 bg-red-50 rounded-2xl">{error}</div>
  );

  return (
    <div className="flex flex-col border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 bg-[#F8FAFC]">
        <MessageSquare className="w-4 h-4 text-[#3FA7D6]" />
        <span className="text-sm font-semibold text-[#0B3C5D]">Messages</span>
        <span className="ml-auto text-xs text-[#6B7280]">{messages.length} message{messages.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Messages area */}
      <div className="overflow-y-auto p-4 space-y-4 bg-[#F8FAFC]" style={{ minHeight: 180, maxHeight: 320 }}>
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-[#6B7280]">No messages yet. Start the conversation!</p>
          </div>
        ) : messages.map((msg) => {
          const isMe = msg.sender_id === user?.user_id;
          return (
            <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.message_id}`}>
              <div className={`max-w-[78%] flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                {!isMe && (
                  <div className="flex items-center gap-1.5 mb-1.5 px-1">
                    <span className="text-xs font-bold text-[#0B3C5D]">{msg.sender_name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[msg.sender_role] || "bg-gray-100 text-gray-600"}`}>
                      {ROLE_LABELS[msg.sender_role] || msg.sender_role}
                    </span>
                  </div>
                )}
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? "bg-[#0B3C5D] text-white rounded-tr-sm" : "bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm"}`}>
                  {msg.text}
                </div>
                <span className="text-xs text-[#6B7280] mt-1 px-1">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 p-3 flex gap-2 bg-white">
        <input
          ref={inputRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
          placeholder="Type a message..."
          data-testid="chat-message-input"
          className="flex-1 px-4 py-2.5 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3FA7D6] focus:ring-2 focus:ring-[#3FA7D6]/10"
        />
        <button
          onClick={sendMessage}
          disabled={!text.trim() || sending}
          data-testid="chat-send-button"
          className="px-4 py-2.5 bg-[#0B3C5D] text-white rounded-xl hover:bg-[#3FA7D6] transition-colors disabled:opacity-50 flex items-center gap-1.5 font-semibold text-sm"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
